import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Sparkles, RefreshCw } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { centsToInputValue, dollarsInputToCents } from "@/lib/money";
import { ProductImageManager } from "@/components/org/ProductImageManager";

const inputClass = "mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 disabled:opacity-60 dark:border-white/15 dark:bg-white/5 dark:text-white";
const labelClass = "text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500";

export const ProductEditModal = ({ product, canEdit, canManageImages, onClose, onUpdated, onRefreshNow }) => {
  const { API } = useAuth();
  const [form, setForm] = useState({
    name: product.name || "",
    category: product.category || "",
    sku: product.sku || "",
    description: product.description || "",
    price: product.price_cents != null ? centsToInputValue(product.price_cents) : "",
  });
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const pollRef = useRef(null);

  const images = product.images || [];
  const primary = images.find((img) => img.is_primary) || images[0];
  const status = product.image_generation_status;

  useEffect(() => {
    if (status === "pending") {
      pollRef.current = setInterval(() => onRefreshNow(), 3000);
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const save = async () => {
    if (!form.name.trim()) { setError("Product name is required."); return; }
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.patch(`${API}/org/products/${product.product_id}`, {
        name: form.name.trim(),
        category: form.category.trim() || null,
        sku: form.sku.trim() || null,
        description: form.description.trim() || null,
        price_cents: form.price !== "" ? dollarsInputToCents(form.price) : null,
      }, { withCredentials: true });
      onUpdated(data.product);
      onClose();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save changes.");
    } finally {
      setSaving(false);
    }
  };

  const triggerGenerate = async () => {
    setGenerating(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/org/products/${product.product_id}/images/generate`, {}, { withCredentials: true });
      onUpdated(data.product);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't start image generation.");
    } finally {
      setGenerating(false);
    }
  };

  const canRegenerate = images.length === 1 && primary?.source === "ai_generated" && status !== "pending";
  const canRetry = images.length === 0 && status === "failed";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4" data-testid="product-edit-overlay">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl dark:bg-[#0B1220]" data-testid="product-edit-modal">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-extrabold text-slate-950 dark:text-white">Edit Product</h2>
          <button type="button" onClick={onClose} data-testid="product-edit-close-btn" className="rounded-full border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 dark:border-white/10 dark:text-slate-400 dark:hover:bg-white/10">
            <X size={16} />
          </button>
        </div>

        <div className="mt-4">
          <label className={labelClass}>Product Image</label>
          <div className="mt-2">
            {status === "pending" && (
              <div className="mb-2 flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 dark:bg-blue-500/10 dark:text-blue-400" data-testid="product-edit-generating-banner">
                <RefreshCw size={12} className="animate-spin" /> Generating an AI image for this product…
              </div>
            )}
            {status === "failed" && images.length === 0 && (
              <div className="mb-2 rounded-lg bg-red-50 px-3 py-2 dark:bg-red-500/10" data-testid="product-edit-generation-error">
                <p className="text-xs font-bold text-red-700 dark:text-red-400">Image generation failed</p>
                <p className="mt-0.5 text-xs text-red-600 dark:text-red-400">{product.image_generation_error || "We couldn't generate an image for this product."}</p>
              </div>
            )}
            {canManageImages ? (
              <ProductImageManager product={product} onUpdated={onUpdated} />
            ) : (
              <p className="text-xs text-slate-400 dark:text-slate-500">You don't have permission to manage this product's image.</p>
            )}
            {canManageImages && (canRegenerate || canRetry) && (
              <button
                type="button"
                onClick={triggerGenerate}
                disabled={generating}
                data-testid="product-edit-regenerate-btn"
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10"
              >
                <Sparkles size={12} /> {generating ? "Starting…" : canRetry ? "Generate Image" : "Regenerate Image"}
              </button>
            )}
          </div>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Product Name *</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} disabled={!canEdit} data-testid="product-edit-name-input" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Category</label>
            <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} disabled={!canEdit} data-testid="product-edit-category-input" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>SKU</label>
            <input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} disabled={!canEdit} data-testid="product-edit-sku-input" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Price ($)</label>
            <input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} disabled={!canEdit} data-testid="product-edit-price-input" className={inputClass} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelClass}>Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} disabled={!canEdit} data-testid="product-edit-description-input" className={inputClass} />
          </div>
        </div>

        {error && <p className="mt-3 text-xs font-semibold text-red-600 dark:text-red-400" data-testid="product-edit-error">{error}</p>}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="product-edit-cancel-btn" className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10">
            {canEdit ? "Cancel" : "Close"}
          </button>
          {canEdit && (
            <button type="button" onClick={save} disabled={saving} data-testid="product-edit-save-btn" className="rounded-xl bg-[#0052FF] px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50">
              {saving ? "Saving…" : "Save Changes"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
